import { useState } from 'react'
import { useRouter } from 'next/router'
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react'
import { usePopper } from 'react-popper'
import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import type { ReactNode } from 'react'

interface NavPopoverProps {
  label: string
  children: ReactNode
}

export function NavPopover({ label, children }: NavPopoverProps) {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  )
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)

  const router = useRouter()

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{ name: 'offset', options: { offset: [-100, 4] } }],
  })

  return (
    <Popover key={router.asPath} className="relative">
      {({ open }) => (
        <>
          <PopoverButton
            ref={setReferenceElement}
            className="link-underline flex items-center whitespace-nowrap py-1 pb-1 text-sm focus:outline-none"
          >
            {label}
            <Icon
              icon="chevron"
              size="small"
              className={tm(
                'ml-1 transform-gpu transition-transform duration-300 ease-out',
                open && 'rotate-180',
              )}
            />
          </PopoverButton>

          <Transition
            as={PopoverPanel}
            className="-left-4 z-50 w-fit transform-gpu rounded-xl bg-white p-4 shadow-md transition-opacity duration-300 ease-out data-[closed]:opacity-0"
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            <div className="flex items-start gap-x-4 whitespace-nowrap">
              {children}
            </div>
          </Transition>
        </>
      )}
    </Popover>
  )
}
